import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Search, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useLocation as useLocCtx } from "@/lib/locationContext";
import { useAuth } from "@/lib/authContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import PortfolioSummaryBar from "@/components/my-locations/PortfolioSummaryBar";
import LocationCard, { getLocationStatus, type LocationStatus } from "@/components/my-locations/LocationCard";
import LocationDetailPanel from "@/components/my-locations/LocationDetailPanel";
import EmptyState from "@/components/my-locations/EmptyState";

const TOTAL_INSPECTION_ITEMS = 20;
type FilterMode = "all" | "healthy" | "at-risk" | "pre-opening";

interface LocStats {
  permitCount: number;
  expiringSoon: number;
  inspectionChecked: number;
}

export default function MyLocations({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { locations, activeLocation, setActiveLocationId, refresh } = useLocCtx();
  const isMobile = useIsMobile();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("Miami");
  const [businessType, setBusinessType] = useState("");
  const [statsMap, setStatsMap] = useState<Record<string, LocStats>>({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterMode>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Fetch stats
  useEffect(() => {
    if (!user || locations.length === 0) return;
    const fetchStats = async () => {
      const locIds = locations.map(l => l.id);
      const [permitsRes, checksRes] = await Promise.all([
        supabase.from("permit_renewals").select("id, location_id, expiration_date").eq("user_id", user.id).in("location_id", locIds),
        supabase.from("health_inspection_checks").select("location_id, checked").eq("user_id", user.id).eq("checked", true).in("location_id", locIds),
      ]);
      const permits = permitsRes.data || [];
      const checks = checksRes.data || [];
      const now = new Date();
      const map: Record<string, LocStats> = {};
      for (const loc of locations) {
        const locPermits = permits.filter((p: any) => p.location_id === loc.id);
        const expiring = locPermits.filter((p: any) => {
          const diff = Math.ceil((new Date(p.expiration_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          return diff >= 0 && diff <= 30;
        });
        map[loc.id] = {
          permitCount: locPermits.length,
          expiringSoon: expiring.length,
          inspectionChecked: checks.filter((c: any) => c.location_id === loc.id).length,
        };
      }
      setStatsMap(map);
    };
    fetchStats();
  }, [user, locations]);

  // Auto-select first location
  useEffect(() => {
    if (!selectedId && locations.length > 0) {
      setSelectedId(activeLocation?.id || locations[0].id);
    }
  }, [locations, activeLocation, selectedId]);

  // Computed data
  const getScore = (locId: string) => {
    const s = statsMap[locId];
    if (!s) return 50;
    const inspPct = (s.inspectionChecked / TOTAL_INSPECTION_ITEMS) * 100;
    const permitPenalty = s.expiringSoon * 10;
    return Math.max(0, Math.min(100, Math.round(inspPct - permitPenalty)));
  };

  const getStatus = (locId: string): LocationStatus => {
    const s = statsMap[locId];
    if (!s) return "pre-opening";
    return getLocationStatus({ inspectionPct: (s.inspectionChecked / TOTAL_INSPECTION_ITEMS) * 100, expiringSoon: s.expiringSoon });
  };

  const allScores = useMemo(() => {
    const m: Record<string, number> = {};
    locations.forEach(l => { m[l.id] = getScore(l.id); });
    return m;
  }, [locations, statsMap]);

  const allStatuses = useMemo(() => {
    const m: Record<string, LocationStatus> = {};
    locations.forEach(l => { m[l.id] = getStatus(l.id); });
    return m;
  }, [locations, statsMap]);

  const filteredLocations = useMemo(() => {
    let list = locations;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(l => l.name.toLowerCase().includes(q) || l.city?.toLowerCase().includes(q) || l.business_type?.toLowerCase().includes(q));
    }
    if (filter !== "all") {
      list = list.filter(l => allStatuses[l.id] === filter);
    }
    return list;
  }, [locations, search, filter, allStatuses]);

  const compact = locations.length >= 10;
  const alertCount = Object.values(statsMap).reduce((n, s) => n + s.expiringSoon, 0);
  const healthScore = locations.length > 0 ? Math.round(Object.values(allScores).reduce((a, b) => a + b, 0) / locations.length) : 0;

  const selectedLocation = locations.find(l => l.id === selectedId) || null;

  // Form handlers
  const resetForm = () => { setName(""); setAddress(""); setCity("Miami"); setBusinessType(""); setShowForm(false); setEditingId(null); };

  const handleSave = async () => {
    if (!user || !name.trim()) { toast.error("Location name is required"); return; }
    if (editingId) {
      const { error } = await supabase.from("locations").update({ name: name.trim(), address: address.trim() || null, city: city.trim(), business_type: businessType.trim() || null }).eq("id", editingId);
      if (error) { toast.error("Failed to update"); return; }
      toast.success("Location updated");
    } else {
      const { error } = await supabase.from("locations").insert({ user_id: user.id, name: name.trim(), address: address.trim() || null, city: city.trim(), business_type: businessType.trim() || null });
      if (error) { toast.error("Failed to add location"); return; }
      toast.success("Location added!");
    }
    resetForm();
    await refresh();
  };

  const handleEdit = () => {
    if (!selectedLocation) return;
    setName(selectedLocation.name);
    setAddress(selectedLocation.address || "");
    setCity(selectedLocation.city || "Miami");
    setBusinessType(selectedLocation.business_type || "");
    setEditingId(selectedLocation.id);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!selectedLocation) return;
    if (!confirm("Delete this location and all its data?")) return;
    await supabase.from("locations").delete().eq("id", selectedLocation.id);
    setSelectedId(null);
    toast.success("Location deleted");
    await refresh();
  };

  const filters: { id: FilterMode; label: string }[] = [
    { id: "all", label: "All" },
    { id: "healthy", label: "Healthy" },
    { id: "at-risk", label: "At Risk" },
    { id: "pre-opening", label: "Pre-Opening" },
  ];

  // Empty state
  if (locations.length === 0 && !showForm) {
    if (embedded) {
      return (
        <div className="flex flex-col">
          <EmptyState onAdd={() => setShowForm(true)} />
          {showForm && renderForm()}
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="border-b border-border bg-card">
          <div className="container py-4 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-medium text-foreground">My Locations</h1>
          </div>
        </div>
        <EmptyState onAdd={() => setShowForm(true)} />
        {showForm && renderForm()}
      </div>
    );
  }

  function renderForm() {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card>
            <CardContent className="pt-5 pb-4 space-y-4">
              <p className="text-sm font-medium text-foreground">{editingId ? "Edit Location" : "New Location"}</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Location Name *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. My First Restaurant" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Business Type</Label>
                  <Input value={businessType} onChange={e => setBusinessType(e.target.value)} placeholder="e.g. Wine Bar" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Address</Label>
                  <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">City</Label>
                  <Input value={city} onChange={e => setCity(e.target.value)} placeholder="Miami" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={!name.trim()} className="gap-1.5">
                  <Check className="w-4 h-4" /> {editingId ? "Update" : "Add"}
                </Button>
                <Button variant="outline" onClick={resetForm}><X className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={embedded ? "flex flex-col" : "min-h-screen bg-background flex flex-col"}>
      {/* Header — only when standalone */}
      {!embedded && (
        <div className="border-b border-border bg-card">
          <div className="container py-3 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-heading text-base font-medium text-foreground">My Locations</h1>
            </div>
            <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} className="gap-1.5 text-xs">
              <Plus className="w-3.5 h-3.5" /> Add
            </Button>
          </div>
        </div>
      )}

      {/* Embedded header with Add button */}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div />
          <Button size="sm" onClick={() => { resetForm(); setShowForm(true); }} className="gap-1.5 text-xs">
            <Plus className="w-3.5 h-3.5" /> Add Location
          </Button>
        </div>
      )}

      {/* Summary Bar */}
      <PortfolioSummaryBar
        totalLocations={locations.length}
        combinedRevenue={0}
        alertCount={alertCount}
        healthScore={healthScore}
      />

      {/* Mobile: horizontal pill selector */}
      {isMobile ? (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Pills */}
          <div className="border-b border-border bg-card px-4 py-2 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {filteredLocations.map(loc => (
                <button
                  key={loc.id}
                  onClick={() => setSelectedId(loc.id)}
                  className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedId === loc.id ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {loc.name}
                  <span className={`text-[10px] ${selectedId === loc.id ? "text-background/70" : ""}`}>
                    {allScores[loc.id] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
          {/* Detail */}
          <div className="flex-1 overflow-y-auto">
            {selectedLocation ? (
              <LocationDetailPanel
                location={selectedLocation}
                status={allStatuses[selectedLocation.id] || "pre-opening"}
                score={allScores[selectedLocation.id] || 0}
                stats={statsMap[selectedLocation.id] ? { ...statsMap[selectedLocation.id], inspectionTotal: TOTAL_INSPECTION_ITEMS } : null}
                allLocations={locations}
                allScores={allScores}
                allStatuses={allStatuses}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground">Select a location</div>
            )}
          </div>
        </div>
      ) : (
        /* Desktop: split panel */
        <div className="flex-1 flex min-h-0">
          {/* Left panel */}
          <div className="w-80 border-r border-border bg-card flex flex-col shrink-0">
            {/* Search */}
            <div className="p-3 space-y-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search locations..."
                  className="pl-8 h-8 text-xs"
                />
              </div>
              <div className="flex gap-1">
                {filters.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                      filter === f.id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Cards list */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {filteredLocations.map((loc, i) => (
                <motion.div key={loc.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <LocationCard
                    location={loc}
                    isActive={selectedId === loc.id}
                    status={allStatuses[loc.id] || "pre-opening"}
                    score={allScores[loc.id] || 0}
                    compact={compact}
                    onClick={() => setSelectedId(loc.id)}
                  />
                </motion.div>
              ))}
              {filteredLocations.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">No locations match your filters.</p>
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex-1 min-w-0">
            {selectedLocation ? (
              <LocationDetailPanel
                location={selectedLocation}
                status={allStatuses[selectedLocation.id] || "pre-opening"}
                score={allScores[selectedLocation.id] || 0}
                stats={statsMap[selectedLocation.id] ? { ...statsMap[selectedLocation.id], inspectionTotal: TOTAL_INSPECTION_ITEMS } : null}
                allLocations={locations}
                allScores={allScores}
                allStatuses={allStatuses}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Select a location to view details
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form modal */}
      {showForm && renderForm()}
    </div>
  );
}
